import React from 'react';
import {
  Target, TrendingUp, TrendingDown, Trophy, Star, Shield, AlertTriangle,
  Zap, Lightbulb, RefreshCw, CheckCircle, Award, Activity, Clock,
  ChevronUp, Swords, Brain, Flame,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// ─── Insight icon lookup ──────────────────────────────────────────
const INSIGHT_ICONS: Record<string, LucideIcon> = {
  target:       Target,
  'trending-up': TrendingUp,
  'trending-down': TrendingDown,
  trophy:       Trophy,
  star:         Star,
  shield:       Shield,
  alert:        AlertTriangle,
  zap:          Zap,
  lightbulb:    Lightbulb,
  refresh:      RefreshCw,
  check:        CheckCircle,
  award:        Award,
  activity:     Activity,
  clock:        Clock,
  'chevron-up': ChevronUp,
  swords:       Swords,
  brain:        Brain,
  flame:        Flame,
};

// ─── StatCard ─────────────────────────────────────────────────────
interface StatCardProps {
  label: string;
  value: string | number;
  sub?: string;
  delta?: number;
  accent?: boolean;
}

export function StatCard({ label, value, sub, delta }: StatCardProps) {
  return (
    <div className="stat-card">
      <div className="stat-card__accent" />
      <div className="stat-card__label">{label}</div>
      <div className="stat-card__value">{value}</div>
      {sub && <div className="stat-card__sub">{sub}</div>}
      {delta !== undefined && (
        <div className={`stat-card__delta stat-card__delta--${delta >= 0 ? 'up' : 'down'}`}>
          {delta >= 0 ? '+' : ''}{delta}
        </div>
      )}
    </div>
  );
}

// ─── Panel ────────────────────────────────────────────────────────
interface PanelProps {
  title?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  action?: React.ReactNode;
}

export function Panel({ title, children, className, style, action }: PanelProps) {
  return (
    <div className={['panel', className].filter(Boolean).join(' ')} style={style}>
      {title && (
        <div className="panel__header">
          <span>{title}</span>
          {action && <span>{action}</span>}
        </div>
      )}
      {children}
    </div>
  );
}

// ─── InsightBox ───────────────────────────────────────────────────
interface InsightBoxProps {
  icon: string;
  text: string;
  action?: string;
  onAction?: () => void;
}

export function InsightBox({ icon, text, action, onAction }: InsightBoxProps) {
  const IconComponent = INSIGHT_ICONS[icon];
  return (
    <div className="insight">
      <div className="insight__header">
        <span className="insight__icon">
          {IconComponent
            ? <IconComponent size={16} strokeWidth={1.75} />
            : icon /* fallback to raw string/emoji if key not found */
          }
        </span>
        <p className="insight__text">{text}</p>
      </div>
      {action && (
        <div className="insight__action" onClick={onAction}>
          → {action}
        </div>
      )}
    </div>
  );
}

// ─── TierBadge ────────────────────────────────────────────────────
interface TierBadgeProps {
  tierName: string;
  tierColor: string;
  nextTierName?: string;
  progress?: number;
}

export function TierBadge({ tierName, tierColor, nextTierName, progress }: TierBadgeProps) {
  return (
    <div className="tier-badge">
      <div
        className="tier-badge__icon"
        style={{ background: tierColor + '22', borderColor: tierColor, color: tierColor }}
      >
        {tierName[0]}
      </div>
      <div>
        <div className="tier-badge__label">Current Tier</div>
        <div className="tier-badge__name" style={{ color: tierColor }}>{tierName}</div>
        {nextTierName && progress !== undefined && (
          <div className="tier-badge__next">{progress}% toward {nextTierName}</div>
        )}
      </div>
    </div>
  );
}

// ─── EmptyState ───────────────────────────────────────────────────
interface EmptyStateProps {
  icon?: string;
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
}

export function EmptyState({ icon, title, subtitle, action }: EmptyStateProps) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state__icon">{icon}</div>}
      <div className="empty-state__title">{title}</div>
      {subtitle && <p className="empty-state__sub">{subtitle}</p>}
      {action}
    </div>
  );
}

// ─── LoadingSpinner ───────────────────────────────────────────────
export function LoadingSpinner({ size = 32 }: { size?: number }) {
  return (
    <div className="spinner-wrap">
      <div className="spinner-ring" style={{ width: size, height: size }} />
    </div>
  );
}